import React, { useMemo, useState } from "react";
import {
  FaBookOpen,
  FaChevronDown,
  FaChevronUp,
  FaGavel,
  FaPlayCircle,
  FaVideo,
} from "react-icons/fa";

const FALLBACK_IMG = "/images/commission-default.png";

const normalizeImageUrl = (url) => {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("//")) {
    return trimmed;
  }

  if (trimmed.startsWith("/")) {
    return `${window.location.origin}${trimmed}`;
  }

  return `${window.location.origin}/${trimmed}`;
};

const extractYoutubeId = (url = "") => {
  const value = String(url || "").trim();
  if (!value) return null;

  const patterns = [
    /youtu\.be\/([^?&/]+)/i,
    /youtube\.com\/watch\?v=([^?&/]+)/i,
    /youtube\.com\/embed\/([^?&/]+)/i,
    /youtube\.com\/shorts\/([^?&/]+)/i,
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match?.[1]) return match[1];
  }

  return null;
};

const getThumbUrl = (quizz) => {
  const directImage = normalizeImageUrl(quizz?.lien_url_image);
  if (directImage) return directImage;

  const youtubeId = extractYoutubeId(quizz?.lien_url_video);
  if (youtubeId) return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;

  return FALLBACK_IMG;
};

export default function QuizzVideoCard({ quizz, onOpenVideo }) {
  const [showAnswer, setShowAnswer] = useState(false);
  const [imgSrc, setImgSrc] = useState(getThumbUrl(quizz));

  const hasExplanation = useMemo(() => {
    return !!String(quizz?.description || "").trim();
  }, [quizz?.description]);

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 w-full bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="relative h-48 overflow-hidden bg-slate-100 dark:bg-slate-800">
        <img
          src={imgSrc}
          alt={quizz?.nom_quizz || "Quizz vidéo"}
          onError={() => setImgSrc(FALLBACK_IMG)}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-transparent" />

        <div className="absolute left-4 top-4">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/15 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
            <FaGavel className="text-[11px]" />
            {quizz?.famille_quizz || "Sans famille"}
          </span>
        </div>

        <div className="absolute bottom-4 right-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm">
            <FaPlayCircle className="text-xl" />
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="line-clamp-2 text-lg font-bold leading-snug text-slate-900 dark:text-white">
          {quizz?.nom_quizz || "Quizz vidéo"}
        </h3>

        {/* <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
            <FaVideo />
            Situation vidéo
          </span>

          <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
            <FaBookOpen />
            Réponse masquée
          </span>
        </div> */}

        {/* <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
          Visualisez d’abord l’action, puis révélez la décision arbitrale et
          l’explication si vous souhaitez vérifier votre analyse.
        </p> */}

        <div className="mt-5 grid grid-cols-1 gap-3">
          <button
            type="button"
            onClick={onOpenVideo}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
          >
            <FaPlayCircle />
            Visualiser la vidéo
          </button>

          <button
            type="button"
            onClick={() => setShowAnswer((prev) => !prev)}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            {showAnswer ? <FaChevronUp /> : <FaChevronDown />}
            {showAnswer ? "Masquer la réponse" : "Afficher la réponse"}
          </button>
        </div>

        {showAnswer && (
          <div className="mt-5 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-500/20 dark:bg-indigo-500/10">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-indigo-700 dark:text-indigo-300">
              Décision arbitrale
            </div>
            <div className="mt-2 text-sm font-bold text-slate-900 dark:text-white">
              {quizz?.decision || "Décision non renseignée"}
            </div>

            <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Explication
            </div>

            {hasExplanation ? (
              <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-300">
                {quizz.description}
              </p>
            ) : (
              <p className="mt-2 text-sm italic text-slate-500 dark:text-slate-400">
                Aucune explication complémentaire disponible.
              </p>
            )}
          </div>
        )}
      </div>
    </article>
  );
}